import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { MoonStar, Gift } from "lucide-react";
import { INSIDE, BONUSES, PRODUCT } from "@/lib/data";

export function InsideGuide() {
  return (
    <section id="inside" data-testid="inside-guide" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:py-32">
      <Reveal>
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-cyan-400/90">Inside The Guide</p>
        <h2 className="font-display mt-3 max-w-2xl text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
          Seven nights. Seven levers. One system.
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-400">
          Each night adds one small technique — read it in five minutes, do it once, and let the stack
          retrain your brain's link between your bed and deep sleep.
        </p>
      </Reveal>

      <div className="mt-14 grid items-start gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <Reveal className="lg:sticky lg:top-24">
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm"
          >
            <div className="absolute -inset-6 rounded-[2rem] bg-cyan-400/10 blur-2xl" />
            <img
              src={PRODUCT.image}
              alt="The 7-Night Sleep Reset — guided sleep program PDF cover"
              data-testid="guide-cover-image"
              className="relative w-full rounded-2xl border border-white/10 shadow-[0_40px_80px_rgba(0,0,0,0.6)]"
            />
            <div className="glass absolute -right-3 top-[52%] hidden rounded-2xl border border-white/10 px-4 py-3 sm:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Format</p>
              <p className="font-display text-lg font-bold text-white">Instant PDF</p>
            </div>
            <div className="glass absolute -left-3 bottom-[18%] hidden rounded-2xl border border-white/10 px-4 py-3 sm:block">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Pages</p>
              <p className="font-display text-lg font-bold text-white">16, zero fluff</p>
            </div>
          </motion.div>
          <div className="mt-4 flex justify-center gap-3 sm:hidden">
            <span data-testid="format-pill-mobile" className="rounded-full border border-white/15 bg-[#131a29] px-4 py-2 text-xs font-semibold text-slate-200">
              <span className="text-slate-500">FORMAT ·</span> Instant PDF
            </span>
            <span data-testid="pages-pill-mobile" className="rounded-full border border-white/15 bg-[#131a29] px-4 py-2 text-xs font-semibold text-slate-200">
              <span className="text-slate-500">PAGES ·</span> 16, zero fluff
            </span>
          </div>
        </Reveal>

        <div className="space-y-3">
          {INSIDE.map((n, i) => (
            <Reveal key={n.night} delay={i * 0.06}>
              <div data-testid={`inside-night-${i + 1}`} className="group flex gap-5 rounded-2xl border border-white/10 bg-[#131a29] p-5 transition-colors hover:border-cyan-400/30">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-400/10">
                  <MoonStar className="h-5 w-5 text-cyan-300" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">{n.night}</p>
                  <h3 className="font-display mt-1 text-base font-bold text-white sm:text-lg">{n.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-400">{n.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.2}>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {BONUSES.map((b) => (
                <div key={b.title} data-testid={`inside-bonus-${b.title.split(" ").join("-").toLowerCase()}`} className="rounded-2xl border border-amber-400/25 bg-amber-400/5 p-5">
                  <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">
                    <Gift className="h-3.5 w-3.5" /> {b.tag}
                  </p>
                  <h3 className="font-display mt-1.5 text-base font-bold text-white">{b.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-400">{b.text}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
